// Shared types for the Settings > Integrations > Sales Channels UI.
// Mirrors the masked shape the BE returns from /admin/integrations.

export interface MaskedSecret {
    set: boolean;
    fingerprint?: string;
    rotatedAt?: string | null;
}

export interface ChannelStatus {
    lastPushOkAt: string | null;
    lastPushFailedAt: string | null;
    lastPushError: string | null;
    lastHealthAt: string | null;
    lastHealthStatus: string | null;
    lastHealthLatencyMs: number | null;
}

export interface VendorSubAccount {
    id: number;
    channelId: number;
    vendorId: string;
    displayName: string;
    enabled: boolean;
    // Ordered priority list of fulfilment warehouses (index 0 = primary).
    // Empty = unrouted (inbound orders for this vendor are rejected).
    fulfilmentWarehouses: string[];
    // Legacy scalar kept in sync with fulfilmentWarehouses[0] by the BE.
    defaultWarehouse: string | null;
}

export type PushMode = "live" | "dry_run";

// Which adapter a channel uses. Card layout and available actions branch
// on THIS, never on channelCode — a Shopify store's code is derived from
// its domain and there can be any number of them.
export type VendorFamily = "jobalots" | "bargainfox" | "shopify" | "amazon";

// One Portal warehouse pinned to one Shopify location. A warehouse with
// no row here is simply not stocked on that store, and its movements are
// never pushed.
export interface ShopifyLocationMapping {
    id: number;
    warehouse: string;
    locationGid: string;
    locationName: string | null;
}

// A location as the store reports it, for the mapping dropdown.
export interface ShopifyLocation {
    gid: string;
    name: string;
    isActive: boolean;
    fulfillsOnlineOrders: boolean;
    city: string | null;
    country: string | null;
}

export interface ShopifyWebhookTopic {
    topic: string;
    expectedUrl: string | null;
    registered: boolean;
    // Non-null when this store is delivering the topic to a DIFFERENT
    // Portal environment — e.g. still pointing at staging after a
    // production cutover, which looks like silence rather than an error.
    wrongEnvironmentUrl: string | null;
}

// One Amazon marketplace Portal can connect to. Region is derived, never
// typed: a marketplace only answers on its own region's host.
export interface AmazonMarketplace {
    id: string;
    region: "eu" | "na" | "fe";
    label: string;
}

export interface AmazonTestResult {
    ok: boolean;
    status: number | null;
    latencyMs: number;
    error: string | null;
    // True when the answer came from the sandbox — a green result there
    // must never be read as a working production channel.
    sandbox?: boolean;
    marketplace: { id: string; name: string; countryCode: string; currency: string } | null;
}

export interface AmazonPollResult {
    ok: boolean;
    seen: number;
    ingested: number;
    skipped: number;
    failed: number;
    error: string | null;
}

// --- Multi-SKU map (shared by every multi-SKU channel family) ----------

export interface SkuMapPortalSku {
    sku: string;
    // Hand-linked rows survive a catalogue re-sync; discovered ones are
    // rebuilt from the channel's own listings each time.
    isManual: boolean;
    // False when no product with this SKU exists in Portal — the single
    // most common reason a listing never receives stock.
    known: boolean;
}

export interface SkuMapListing {
    externalSku: string;
    asin: string | null;
    title: string | null;
    tracked: boolean;
    // Several Portal SKUs behind one listing IS the multi-SKU feature:
    // stock pushed is their sum, and an order line splits across them.
    portalSkus: SkuMapPortalSku[];
}

export interface ShopifyCatalogueSyncResult {
    mapped: number;
    matched: number;
    unmatched: number;
    duplicates: string[];
    channel: SalesChannelConfig;
}

export interface SalesChannelConfig {
    id: number;
    channelCode: string;
    displayName: string;
    vendorFamily: VendorFamily;
    enabled: boolean;
    // Built-in partner channels are permanent; stores added from this UI
    // can be removed again (while no order references them).
    isRemovable: boolean;
    // --- Shopify-only. Empty/null on partner channels. ---------------
    shopDomain: string;
    apiVersion: string;
    shopifyRequirePaid: boolean;
    shopifyNotifyCustomer: boolean;
    catalogueSyncedAt: string | null;
    catalogueSkuCount: number | null;
    locations: ShopifyLocationMapping[];
    // --- Amazon-only. Empty/null on other families. ------------------
    amazonSellerId: string;
    amazonMarketplaceId: string;
    amazonRegion: string;
    amazonLwaClientId: string;
    amazonOrdersCursor: string | null;
    amazonOrdersPolledAt: string | null;
    amazonOrdersPollError: string | null;
    // Points this channel at Amazon's sandbox hosts (canned data).
    amazonUseSandbox: boolean;
    // Consolidate on address alone, ignoring the buyer email. Required
    // on Amazon (per-order anonymised aliases); off by default because
    // it loosens identity.
    consolidationMatchAddressOnly: boolean;
    pushMode: PushMode;
    // Merge unfulfilled orders sharing a customer email + delivery
    // address into one shipment for dispatch (Portal-internal; the
    // channel still receives per-original-order shipment pushes). This is
    // the PARCEL rule; the cutoff fields below apply to it.
    consolidateOrders: boolean;
    // PALLET rule (migration 229): all-pallet orders for one buyer merge
    // into one consignment — no cutoff, pallet-with-pallet only.
    consolidatePalletOrders: boolean;
    // Warehouses the pallet rule skips (migration 276): toggle on, but
    // pallet orders landing at these ship standalone (e.g. MCL, where
    // every pallet order is fulfilled by hand). Warehouse names; empty =
    // consolidate everywhere.
    palletConsolidationExcludedWarehouses: string[];
    // AI auto-assessment of "Extra protection needed" for products
    // ordered through this channel (one-off Claude call per product).
    aiExtraProtection: boolean;
    // Whether this channel's orders HONOUR the flag at the pack desk.
    // Independent of aiExtraProtection: turn assessment off and leave
    // this on to keep respecting existing + hand-set flags without
    // paying for further classification.
    enforceExtraProtection: boolean;
    // Optional cutoff window for consolidation: "HH:MM" wall-clock in
    // consolidationTimezone. Orders only merge within the same
    // cutoff→cutoff dispatch window; null = no clock limit (merge with
    // any still-unfulfilled order).
    consolidationCutoffTime: string | null;
    consolidationTimezone: string;
    // Weekdays the cutoff fires on; windows span firing to firing
    // (e.g. ["mon","wed","fri"] = Fri 5am-Mon 5am, Mon-Wed, Wed-Fri).
    consolidationCutoffDays: string[];
    baseUrl: string;
    // --- Customer-email branding -------------------------------------
    // Sender of the replacement-shipped email. Blank = this channel sends
    // no customer email at all (nothing is guessed from another brand).
    notifyFromAddress: string;
    notifyFromName: string;
    // Reply-To for those emails — the brand's customer-service mailbox,
    // so a reply to a no-reply sender still reaches a person. Empty
    // string = no Reply-To header.
    notifyReplyToAddress: string;
    // Absolute, already-public URL of the brand logo, pasted by an admin
    // (deliberately not an upload — an email client fetching it has no
    // session and can be on any network). Empty string = none set.
    logoUrl: string;
    // --- Customer shipment emails (migration 164) ---------------------
    // Whether Portal itself emails the buyer when an order ships. Split in
    // two because the cases fail opposite ways: a non-API order has nobody
    // else to tell (default on), an API order already has its channel
    // telling them (default off).
    notifyCustomerNonApi: boolean;
    notifyCustomerApi: boolean;
    // Parcel tracking to the channel (POST /order-status, migration 267):
    // in transit / out for delivery / delivered at the carrier's time.
    // BargainFox only; off by default.
    pushOrderStatus: boolean;
    // The channel's own sales invoices and credit notes, downloadable from
    // the Orders page order detail (GET /orders/:id/channel-documents). The
    // channel generates each PDF on request; Portal never stores them.
    // BargainFox only; off by default.
    channelDocuments: boolean;
    // BargainFox Returns exchange orders on BargainFox's orders page (JE-3352). BargainFox only; off by default.
    pushExchangeOrders?: boolean;
    // ...and Portal's replacement orders (REP…) there too (migration 274). BargainFox only; off by default.
    pushReplacementOrders?: boolean;
    // Free-text orders.sales_channel values that mean this channel — the
    // only link a non-API order has to one. 'Jobalots UK' on the Jobalots
    // channel is the case this exists for.
    salesChannelAliases: string[];
    outboundSecret: MaskedSecret;
    inboundSecret: MaskedSecret;
    status: ChannelStatus;
    vendors: VendorSubAccount[];
}

export interface ConsolidationStats {
    days: number;
    totalOrders: number;
    consolidatedOrders: number;
    masters: number;
    shipments: number;
    parcelsSaved: number;
    pctSaved: number;
}

// The customer emails the channel card can send a sample of. Ids are the
// BE contract (customerEmailSamples.TEST_EMAIL_SCENARIOS); labels are
// i18n keys `channel_test_email_scenario_<id>`.
export const TEST_EMAIL_SCENARIOS = [
    "shipment",
    "shipment_multi",
    "shipment_partial",
    "pallet",
    "consolidated",
    "consolidated_partial",
    "consolidated_many",
    "replacement",
] as const;
export type TestEmailScenario = typeof TEST_EMAIL_SCENARIOS[number];

// The languages the customer email is written in — the BE contract
// (customerEmailCopy.EMAIL_COPY), which follows the Jobalots site's 22.
// Real Jobalots sends use the buyer's own choice; this only picks the
// sample. Each name is in its own language, so no i18n keys.
export const TEST_EMAIL_LANGUAGES = [
    { code: "en", name: "English" },
    { code: "bg", name: "Български" },
    { code: "cs", name: "Čeština" },
    { code: "da", name: "Dansk" },
    { code: "de", name: "Deutsch" },
    { code: "el", name: "Ελληνικά" },
    { code: "es", name: "Español" },
    { code: "et", name: "Eesti" },
    { code: "fi", name: "Suomi" },
    { code: "fr", name: "Français" },
    { code: "hr", name: "Hrvatski" },
    { code: "hu", name: "Magyar" },
    { code: "it", name: "Italiano" },
    { code: "lt", name: "Lietuvių" },
    { code: "lv", name: "Latviešu" },
    { code: "nl", name: "Nederlands" },
    { code: "pl", name: "Polski" },
    { code: "pt", name: "Português" },
    { code: "ro", name: "Română" },
    { code: "sk", name: "Slovenčina" },
    { code: "sl", name: "Slovenščina" },
    { code: "sv", name: "Svenska" },
] as const;
export type TestEmailLanguage = typeof TEST_EMAIL_LANGUAGES[number]["code"];

export interface TestConnectionResult {
    ok: boolean;
    status: number | null;
    latencyMs: number;
    error: string | null;
}

export type ChannelPatch = {
    enabled?: boolean;
    pushMode?: PushMode;
    consolidateOrders?: boolean;
    consolidatePalletOrders?: boolean;
    palletConsolidationExcludedWarehouses?: string[];
    aiExtraProtection?: boolean;
    enforceExtraProtection?: boolean;
    consolidationCutoffTime?: string | null;
    consolidationTimezone?: string;
    consolidationCutoffDays?: string[];
    baseUrl?: string;
    notifyFromAddress?: string;
    notifyFromName?: string;
    notifyReplyToAddress?: string;
    logoUrl?: string;
    notifyCustomerNonApi?: boolean;
    notifyCustomerApi?: boolean;
    pushOrderStatus?: boolean;
    channelDocuments?: boolean;
    pushExchangeOrders?: boolean;
    pushReplacementOrders?: boolean;
    salesChannelAliases?: string[];
    outboundSecret?: string | null;
    inboundSecret?: string | null;
    // Shopify-only.
    displayName?: string;
    shopDomain?: string;
    apiVersion?: string | null;
    shopifyRequirePaid?: boolean;
    shopifyNotifyCustomer?: boolean;
    // Amazon-only.
    amazonSellerId?: string;
    amazonMarketplaceId?: string;
    amazonRegion?: string;
    amazonLwaClientId?: string;
    amazonUseSandbox?: boolean;
    consolidationMatchAddressOnly?: boolean;
};

export type VendorPatch = {
    vendorId?: string;
    displayName?: string;
    enabled?: boolean;
    fulfilmentWarehouses?: string[];
    defaultWarehouse?: string | null;
};

// ---- Carriers (Settings > Integrations > Carriers) -------------------
// Mirrors the masked shape the BE returns from /admin/carriers.

export type CarrierEnvironment = "training" | "live";

export interface CarrierService {
    id: number;
    accountId: number;
    serviceCode: string;       // e.g. "NPND16" — per-depot APC code
    displayName: string;
    itemType: string;          // APC Item.Type wire value: PARCEL | PACK | ...
    maxPieces: number | null;
    maxWeightGrams: number | null;
    maxLengthCm: number | null;
    maxWidthCm: number | null;
    maxHeightCm: number | null;
    // Recorded tariff (£): base per consignment, per extra parcel (0 on
    // ND/ROAD — the multi-box discount), and per-kg above the included
    // allowance. Informational — Shipping Rules dropdown + tester.
    baseCost: number | null;
    additionalParcelCost: number | null;
    includedKg: number | null;
    perKgCost: number | null;
    // InPost per-parcel band pricing (migration 185): S/M/L rates for a
    // to-address service, or a flat locker/shop rate. Null for APC.
    pricing: CarrierServicePricing | null;
    enabled: boolean;
    sortOrder: number;
}

export interface CarrierServicePricing {
    priority?: "STANDARD" | "EXPRESS";
    bands?: { S?: number; M?: number; L?: number };
    flat?: number;
    vas?: string[];
    // DPD Poland: the services[] sent on every package (spec "Available
    // Services"), pickup → DPD_PICKUP with the order's point id.
    dpd?: {
        services?: { code: string; attributes?: { code: string; value: string }[] }[];
        pickup?: boolean;
        content?: string;
        // Which contract: "EU-DPD Parcel Classic (Local)" = domestic (PL),
        // "(INTER)" = international (EU). Absent = either.
        scope?: "domestic" | "international";
        // Payer FID for this contract when it differs from the account's.
        payer_fid?: number;
    };
    // Cargus: the ServiceId + AWB options.
    cargus?: {
        service_id?: number;
        saturday?: boolean;
        morning?: boolean;
        open_package?: boolean;
        declared_value?: boolean;
        content?: string;
        observations?: string;
    };
}

export type CarrierDeliveryMode = "address" | "point";

export interface CarrierAccountStatus {
    lastTestAt: string | null;
    lastTestStatus: string | null;
    lastTestError: string | null;
}

export interface CarrierAccount {
    id: number;
    carrierId: number;
    warehouse: string;
    // Several accounts per (carrier, warehouse) since migration 185 —
    // InPost's "2door" / "ooh" credentials; "default" for APC.
    accountLabel: string;
    deliveryMode: CarrierDeliveryMode;
    enabled: boolean;
    loginEmail: string;
    // OAuth identity (InPost). The client SECRET is `password` below.
    clientId: string;
    organizationId: string;
    password: MaskedSecret;
    // DPD Poland / Cargus (migration 186): a plain username, the APIM
    // subscription key (Cargus, masked like the password), DPD's master
    // + payer FIDs, Cargus's pickup point + price table, and the sender
    // company / origin county the label prints or routes on.
    loginUsername: string;
    apiKey: MaskedSecret;
    masterFid: number | null;
    payerFid: number | null;
    // DPD InfoServices (tracking) channel; null until DPD confirm it.
    trackingChannel: string | null;
    pickupLocationId: number | null;
    priceTableId: number | null;
    senderCompany: string;
    originCounty: string;
    // The shipper's VAT number / NIP (BE migration 246) — JAS's loading
    // place is billed B2B on it.
    vatNumber: string;
    // Sender contact + origin address printed on every InPost label.
    senderFirstName: string;
    senderLastName: string;
    senderEmail: string;
    senderPhone: string;
    originHouseNumber: string;
    originStreet: string;
    originCity: string;
    originPostcode: string;
    originCountryCode: string;
    readyAt: string | null;    // "HH:MM" | null → APC account defaults
    closedAt: string | null;
    // Customs identity + default duty line — groundwork for Northern
    // Ireland (ROAD) customs data; not sent on bookings until APC
    // provide the API schema. The duty line is applied as ONE aggregate
    // declaration per consignment (units/unit-value computed at booking).
    eoriNumber: string;
    ioss: boolean;
    dutyDescription: string;
    dutyHtsCode: string;
    dutyOrigin: string;
    status: CarrierAccountStatus;
    services: CarrierService[];
    // Per-account carrier settings (Raben: config.raben). Written back whole
    // by PATCH with the carrier key merged in; other keys pass through.
    config?: CarrierAccountRuntimeConfig;
}

export interface CarrierConfig {
    id: number;
    carrierCode: string;       // 'apc'
    displayName: string;
    enabled: boolean;
    environment: CarrierEnvironment;
    trainingBaseUrl: string;
    liveBaseUrl: string;
    // Add-box-to-existing-consignment feature toggles.
    addBoxToExistingConsignment: boolean;
    crossChannelAddBoxToExistingConsignment: boolean;
    // Carrier claims (migration 183): scheduled email of the carrier's
    // claims sheet. Config always arrives complete (BE fills defaults).
    claimsEnabled: boolean;
    claimsConfig: CarrierClaimsConfig;
    claimsLastRunAt: string | null;
    // Migration 185: API path prefix (InPost's two documented create
    // paths), surcharge config, tracking-webhook secret + last delivery.
    apiPathPrefix: string;
    config: CarrierRuntimeConfig;
    // What the cost model actually uses: stored surcharges over the contract
    // defaults (incl. InPost's published remote-area list when the row holds
    // none). `remoteAreaCustom` says the row overrides that list.
    effectiveSurcharges?: CarrierSurchargeConfig;
    remoteAreaDefaultCount?: number;
    remoteAreaCustom?: boolean;
    webhookSecret: MaskedSecret;
    webhookLastReceivedAt: string | null;
    accounts: CarrierAccount[];
}

export interface CarrierSurchargeConfig {
    fuel_pct?: number;
    // APC: the monthly fuel surcharge applies from this date (YYYY-MM-DD)
    fuel_from?: string | null;
    decarbonisation?: number;
    northern_ireland?: number;
    remote_area?: number;
    // null on write = restore InPost's published list
    remote_area_postcodes?: string[] | null;
    remote_area_unverified?: boolean;
    oog1?: number;
    oog2?: number;
    // literal Schedule 1 reading: OOG1/OOG2 on every Medium/Large parcel
    oog_on_band?: boolean;
    oog3?: number;
    oog4?: number;
    oog_ooh?: number;
}

export type RabenProductType = "PROD01" | "PROD02";   // Cargo Classic / Cargo Premium

/** Raben eWZ (electronic delivery note) SFTP upload. The SFTP password is the account's apiKey. */
export interface RabenEwzConfig {
    enabled: boolean;
    host: string;
    port: number;
    username: string;
    relationNumber: string;   // 11 chars
    remotePath: string;
}

/** account.config.raben — the EDI header, payer, product and eWZ settings of a Raben account. */
export interface RabenAccountConfig {
    ediSender: string;
    ediReceiver: string;
    department: string;
    payerSearchname: string;
    ediCustomerNumber: string;
    shipFromSearchname: string;
    productType: RabenProductType;
    services: string[];
    notes: string[];
    ewz: RabenEwzConfig;
}

export interface CarrierAccountRuntimeConfig {
    raben?: Partial<RabenAccountConfig> & { ewz?: Partial<RabenEwzConfig> };
    [key: string]: unknown;
}

export interface CarrierRuntimeConfig {
    surcharges?: CarrierSurchargeConfig;
    [key: string]: unknown;
}

export type CarrierClaimsFrequency = "daily" | "weekly" | "monthly";

export interface CarrierClaimsConfig {
    fromAddress: string;
    fromName: string;
    toAddress: string;
    ccAddress: string;
    replyToAddress: string;
    subject: string;
    bodyText: string;
    frequency: CarrierClaimsFrequency;
    weeklyDay: number;   // 0 Sunday … 6 Saturday
    monthlyDay: number;  // 1 … 28
    sendTime: string;    // HH:MM Europe/London
}

export interface CarrierClaimsStatus {
    enabled: boolean;
    pendingCount: number;
    oldestPendingAt: string | null;
    lastRunAt: string | null;
    nextDueAt: string | null;
    mailConfigured: boolean;
    // False until the carrier's claims address is entered (non-APC carriers
    // start with none); the scheduled run and Send now cannot send before.
    hasRecipient?: boolean;
    lastSubmission: {
        submission_id: string;
        claim_count: number;
        filename: string;
        // The supplier invoices PDF emailed with the sheet; null when that
        // submission had no invoice-backed claim.
        invoices_filename?: string | null;
        sent_to: string | null;
        sent_cc: string | null;
        subject: string | null;
        message_id: string | null;
        send_error: string | null;
        trigger: string;
        created_at: string;
    } | null;
}

export type CarrierPatch = {
    enabled?: boolean;
    environment?: CarrierEnvironment;
    addBoxToExistingConsignment?: boolean;
    crossChannelAddBoxToExistingConsignment?: boolean;
    claimsEnabled?: boolean;
    claimsConfig?: Partial<CarrierClaimsConfig>;
    apiPathPrefix?: string | null;
    config?: CarrierRuntimeConfig;
    webhookSecret?: string | null; // write-only plaintext; null clears
};

export type CarrierAccountPatch = {
    enabled?: boolean;
    loginEmail?: string | null;
    password?: string | null;  // write-only plaintext; null clears
    readyAt?: string | null;
    closedAt?: string | null;
    eoriNumber?: string | null;
    ioss?: boolean;
    dutyDescription?: string | null;
    dutyHtsCode?: string | null;
    dutyOrigin?: string | null;
    clientId?: string | null;
    organizationId?: string | null;
    deliveryMode?: CarrierDeliveryMode;
    senderFirstName?: string | null;
    senderLastName?: string | null;
    senderEmail?: string | null;
    senderPhone?: string | null;
    originHouseNumber?: string | null;
    originStreet?: string | null;
    originCity?: string | null;
    originPostcode?: string | null;
    originCountryCode?: string | null;
    // DPD Poland / Cargus (migration 186).
    loginUsername?: string | null;
    apiKey?: string | null;        // write-only plaintext; null clears
    masterFid?: number | null;
    payerFid?: number | null;
    trackingChannel?: string | null;
    pickupLocationId?: number | null;
    priceTableId?: number | null;
    senderCompany?: string | null;
    originCounty?: string | null;
    // JAS (BE migration 246).
    vatNumber?: string | null;
    // Raben: the whole account config with config.raben merged in.
    config?: CarrierAccountRuntimeConfig;
};

export type CarrierServicePatch = {
    serviceCode?: string;
    displayName?: string;
    itemType?: string;
    maxPieces?: number | null;
    maxWeightGrams?: number | null;
    maxLengthCm?: number | null;
    maxWidthCm?: number | null;
    maxHeightCm?: number | null;
    baseCost?: number | null;
    additionalParcelCost?: number | null;
    includedKg?: number | null;
    perKgCost?: number | null;
    pricing?: CarrierServicePricing | null;
    enabled?: boolean;
};

export interface CarrierTestResult {
    ok: boolean;
    status: number | null;
    latencyMs: number;
    error: string | null;
    environment: CarrierEnvironment;
}
